/* eslint-disable */

import React, {
  useContext, useState,
} from 'react'
import styled from 'styled-components'
import { get } from 'lodash'
import { jsPDF as JsPDF } from 'jspdf'
import html2canvas from 'html2canvas'

import { useRequest } from '../hooks/request'
import { facmGet, facmFilesGet } from '../requests/facmGet'
import { userGet } from '../requests/user'
import { dateToDateTime } from '../utils/dateToDDMMYYYY'
import {
  VARIANT_SECONDARY, VARIANT_DANGER,
} from '../constants/variants'
import { UserContext } from '../data/UserContext'
import { ROLES } from '../constants/roles'
import { createRequest } from '../requests/createRequest'
import { getFullName } from '../utils/getFullName'
import { emptySection } from '../utils/emptySection'
import { theme } from '../constants/theme'
import { ACTIVITY_LOG_ACTION } from '../constants/activityLogActions'
import { PATHS } from '../constants/paths'
import { QUALIF_SKILL } from '../constants/mountainSkill'

import { PageTitle } from './common/PageTitle'
import { PageContent } from './layout/layout'
import { Loader } from './icons/Loader'
import { Trad } from './common/Trad'
import {
  H3, H4, Body, Label,
} from './common/Typography'
import { Margin } from './common/Margin'
import { Input } from './common/Input'
import { FieldWrapper } from './fields/FieldWrapper'
import { ConfirmButton } from './common/ConfirmButton'
import { Button } from './common/Button'
import { CenterStuff } from './common/CenterStuff'
import { ErrorText } from './common/ErrorText'
import { Block } from './common/Block'
import { useParams } from 'react-router-dom'

const roleToAcceptPathMap = {
  [ROLES.MOUNTAIN_OFFICER]: '/facm/approve/mountain_officer',
  [ROLES.CORPS_CHIEF]: '/facm/approve/corps_chief',
  [ROLES.UNIT_COMMANDER]: '/facm/approve/unit_commander',
}

const roleToRejectPathMap = {
  [ROLES.MOUNTAIN_OFFICER]: '/facm/reject/mountain_officer',
  [ROLES.CORPS_CHIEF]: '/facm/reject/corps_chief',
  [ROLES.UNIT_COMMANDER]: '/facm/reject/unit_commander',
}

export const FacmDetail = () => {
  const params = useParams()
  const { user } = useContext(UserContext)
  const [comment, setComment] = useState()
  const facmId = params.id
  const facmRequest = useRequest({
    request: () => facmGet({ params: { id: facmId } }),
    start: true,
  })

  const facmFilesRequest = useRequest({
    request: () => facmFilesGet({ params: { id: facmId } }),
    start: true,
  })

  const approveRequest = useRequest({
    request: createRequest({ path: roleToAcceptPathMap[user.role] }),
    handler: (p) => p.then((newFacm) => facmRequest.setData({ data: newFacm })),
  })

  const rejectRequest = useRequest({
    request: createRequest({ path: roleToRejectPathMap[user.role] }),
    handler: (p) => p.then((newFacm) => facmRequest.setData({ data: newFacm })),
  })

  if (facmRequest.state.loading || !facmRequest.state.data
    || facmFilesRequest.state.loading || !facmFilesRequest.state.data) {
    return (
      <CenterStuff>
        <Loader height={100} />
      </CenterStuff>
    )
  }
  const facm = facmRequest.state.data
  const facmFiles = facmFilesRequest.state.data
  const shouldDisplayPractical = !(facm.memoNumber && facm.totalHeadcount)
  const skills = facm.skillsCounts.reduce(
    (acc, skill, index) => `${acc} #${index + 1}. ${QUALIF_SKILL[skill.skillName]} :  ${skill.skillNumber} pers. \n`, '',
  )
  const participants = facm.participants?.reduce((acc, p, index) => `${acc} #${index + 1}. ${p.fullName} \n`, '')
  const steps = facm.type === 'CLASSIC'
    ? facm.checkpoints.reduce(
      (acc, step, index) => `${acc} #${index + 1}. ${step.checkpointName} : ${step.checkpointGdh} \n`, '',
    )
    : facm.checkpoints.reduce(
      (acc, step, index) => `${acc} #${index + 1}. ${step} \n`, '',
    )
  const filesItems = () => facmFiles.files.map((f, index) => (<DelayedImage file={f} key={index} />))

  const exportPDF = () => {
    const facmPage = document.querySelector('#facm');

    html2canvas(facmPage).then(function(canvas) {
      let imgData = canvas.toDataURL('image/png');
      let doc = new JsPDF('p', 'mm', 'a4');
      let imgWidth = 210;
      let pageHeight = 295;
      let imgHeight = canvas.height * imgWidth / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        doc.addPage();
        doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      doc.save( `FACM-${facm.facmNumber}.pdf`);
    })
  }

  return (
    <div id="facm">
      <div>
        <PageTitle
          title={facm.mountainPeak}
          subTitle={(<Trad id="raceNumber" values={{ number: facm.facmNumber }} />
          )}
          status={facm.status}
          history={facm.history}
          backLink={PATHS.FACM_ALL}
          backLinkTrad="facmList"
        >
          <Button
            onClick={() => {
              exportPDF()
            }}
            tradId="export"
            variant={VARIANT_SECONDARY}
            data-html2canvas-ignore="true"
          />
        </PageTitle>
      </div>
      <Margin h={10} />
      <PageContent>
        <div>
          <Block shadow>
            <H3 variant={VARIANT_SECONDARY} block bold>
              <Trad id="history" />
            </H3>
          </Block>
          <Margin v={15} />
          <Block border>
            {facm.history.map((entry) => (
              <div key={entry.eventDate}>
                <ItemWrapper>
                  <ItemContent bold>
                    <Trad
                      id={`history_${entry.eventType}`}
                      values={{
                        fullName: `${entry.who.lastName} ${entry.who.firstName}`,
                        startDate: dateToDateTime(get(entry, 'data.startDate')),
                        endDate: dateToDateTime(get(entry, 'data.endDate')),
                        message: get(entry, 'data.text'),
                      }}
                    />
                  </ItemContent>
                  <Margin h={5} />
                  <div>{`- Date : ${dateToDateTime(entry.eventDate)}`}</div>
                </ItemWrapper>
                {facm.status === 'ALERT' && (entry.eventType === 'incident' || entry.eventType === 'accident') && (
                  <ItemWrapper>
                    <ItemContent bold variant={VARIANT_DANGER} upper>
                      <Trad
                        id={`display_${facm.status}`}
                        values={{
                          reportType: `${entry.eventType}`,
                        }}
                      />
                    </ItemContent>
                    <Margin h={5} />
                  </ItemWrapper>
                )}
                {entry.comment && (
                  <Label
                    size={theme.typography.h4}
                    variant={entry.eventType === ACTIVITY_LOG_ACTION.MOUNTAIN_OFFICER_REJECT ? VARIANT_DANGER : ''}
                  >
                    {`Commentaire : "${entry.comment}"`}
                  </Label>
                )}
              </div>
            ))}
            <Margin v={20} />
            {facm.status === `WAITING_${user.role}` && (
              <div>
                <FieldWrapper>
                  <Input
                    name="comment"
                    value={comment}
                    placeholder="enterComment"
                    onChange={(e) => setComment(e.target.value)}
                  />
                  {!facm.icom && facm.relay && <ErrorText error="icomMissing" />}
                  {get(rejectRequest.state.data, 'errorCode') === 10000 && <ErrorText error="commentRequired" />}
                </FieldWrapper>
                <ConfirmButton
                  variant={VARIANT_DANGER}
                  titleTrad="rejectConfirm"
                  tradId="reject"
                  loading={rejectRequest.state.loading}
                  onConfirm={() => rejectRequest.fetch({ id: facm.id, comment })}
                />
                <Margin h={30} />
                <ConfirmButton
                  variant={VARIANT_SECONDARY}
                  titleTrad="approveConfirm"
                  filled
                  tradId="approve"
                  loading={approveRequest.state.loading}
                  onConfirm={() => approveRequest.fetch({ id: facm.id, comment })}
                />
              </div>
            )}
          </Block>
        </div>
        <Margin v={20} />
        <Block>
          <H3 variant={VARIANT_SECONDARY} block bold>
            <Trad id="requesterInformation" />
          </H3>
        </Block>
        <Margin v={15} />
        <Section
          items={[
            { title: 'name', content: (facm.author ? getFullName({ user: facm.author }) : '-') },
            { title: 'diploma', content: (facm.author ? facm.author.diplomas : '-') },
            { title: 'phone', content: (facm.author ? <a href={`tel:${facm.author.phone}`}>{facm.author.phone}</a> : '-') },
          ]}
        />
        <Margin v={30} />
        <Block>
          <H3 variant={VARIANT_SECONDARY} block bold>
            <Trad id="summary" />
          </H3>
        </Block>
        <Margin v={15} />
        {shouldDisplayPractical && (
          <Section
            title="participantList"
            idx="1"
            items={[
              { title: 'unit', content: facm.unit },
              { title: 'organicChief', content: facm.organicChief },
              { title: 'civilianInformation', trad: `${facm.withCivilians}` },
              { title: 'totalHeadcount', content: facm.totalHeadcount },
              { title: 'diploma', content: skills },
              ...(participants ? [{ title: 'participantList', content: participants }] : []),
            ]}
          />
        )}
        <Section
          title="raceDetails"
          idx="2"
          items={[
            { title: 'goal', content: facm.mountainPeak },
            { title: 'activityFrame', trad: facm.activityFrame && `race_${facm.activityFrame}` },
            { title: 'raceType', trad: facm.raceType && `raceType.${facm.raceType}` },
            { title: 'mountains', content: facm.mountains },
            { title: 'locality', content: facm.locality },
            { title: 'routeName', content: facm.routeName },
            { title: 'difficulty', content: facm.difficulty },
            { title: 'incline', content: facm.incline },
            { title: 'checkpoints', content: steps },
            { title: 'alternateObjective', content: facm.alternateObjective },
            { title: 'responsabilityArea', content: facm.responsabilityArea },
            ...facmFiles.files ? [{ title: 'attachedFiles', content: filesItems() }] : [],
          ]}
        />
        <Section
          title="dateInformation"
          idx="3"
          items={[
            { title: 'startEndDate', content: `${dateToDateTime(facm.startDate)} au ${dateToDateTime(facm.endDate)}` },
            ...facm.rescheduleStartDate ? [{
              title: 'rescheduleDate',
              content: `${dateToDateTime(facm.rescheduleStartDate)} au ${dateToDateTime(facm.rescheduleEndDate)}`,
            }] : [],
          ]}
        />
        <Section
          title="gear"
          idx="4"
          items={[
            { title: 'soloGear', content: facm.soloGear },
            { title: 'groupGear', content: facm.groupGear },
            { title: 'rescueGear', content: facm.rescueGear },
          ]}
        />
        <Section
          title="rescueInformation"
          idx="5"
          items={[
            { title: 'unitCommander', content: (facm.cdu ? getFullName({ user: facm.cdu }) : 'Non renseigné') },
            { title: 'relay', content: facm.relay },
            { title: 'aidType', content: facm.aidType },
            { title: 'phone', content: facm.aidContact },
            { title: 'portPhone', content: facm.phone },
            { title: 'satPhone', content: facm.satPhone },
            { title: 'icom', content: facm.relay && (facm.icom ? facm.icom : 'Non renseigné'), important: !facm.icom },
            { title: 'gdh', content: facm.gdh && dateToDateTime(facm.gdh) },
            { title: 'frequency', content: facm.frequency },
            { title: 'detachmentLabel', content: facm.detachmentLabel },
            { title: 'relayLabel', content: facm.relayLabel },
            { title: 'relayPhone', content: facm.relayPhone },
            { title: 'relayLocation', content: facm.relayLocation },
          ]}
        />
        <Section
          title="admin"
          idx="6"
          items={[
            { title: 'vehicle', content: facm.vehicle },
            { title: 'highwayCard', trad: facm.highwayCard !== undefined && facm.highwayCard.toString() },
            { title: 'housing', trad: facm.housing },
            { title: 'isHoused', trad: facm.isHoused !== undefined && facm.isHoused.toString() },
            { title: 'food', trad: facm.food },
            { title: 'isFed', trad: facm.isFed !== undefined && facm.isFed.toString() },
            { title: 'budget', content: facm.budget },
            { title: 'budgetNotes', content: facm.budgetNotes },
            { title: 'rate', trad: facm.rate && `rate_${facm.rate}` },
            { title: 'plateAndType', content: facm.plateAndType },
            { title: 'fuelCard', trad: facm.fuelCard !== undefined && facm.fuelCard.toString() },
          ]}
        />

      </PageContent>
    </div>
  )
}

const Section = ({
  title, idx, items, iterateOver, getItems,
}) => {
  const shouldBeDisplayed = iterateOver || items
  if (!shouldBeDisplayed) {
    return null
  }

  return (
    <SectionWrapper>
      {idx && (
        <Index variant={VARIANT_SECONDARY} bold>{idx}</Index>
      )}
      {title && (
        <H4 variant={VARIANT_SECONDARY} tradId={title} block bold />
      )}
      <Margin v={10} />
      {iterateOver
        ? iterateOver.map((i, index) => <Items items={getItems(i)} key={index} />)
        : <Items items={items} />}
      {items && emptySection(items)
        ? <ItemContent tradId="emptySection" block fontSize={theme.typography.body} />
        : null}
    </SectionWrapper>
  )
}


const Items = ({ items }) => (
  <ItemsWrapper>
    {items.map((item) => (
      (item.content || item.trad) && (
        <ItemWrapper key={item.title}>
          <ItemContent
            tradId={item.title}
            bold
            fontSize={theme.typography.body}
            variant={item.important ? VARIANT_DANGER : ''}
          >
            {' : '}
          </ItemContent>
          <Margin h={5} />
          {item.trad
            ? <ItemContent tradId={item.trad} block fontSize={theme.typography.body} />
            : <ItemContent block fontSize={theme.typography.body}>{item.content}</ItemContent>}
        </ItemWrapper>
      )
    ))}
  </ItemsWrapper>
)

const ItemsWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const ItemWrapper = styled.div`
  display: flex;
  margin-top: 10px;
  min-width: 259px;
  max-width: 90%;
  margin-right: 20px;
  flex-direction: row;
  @media (max-width: 1000px) {
    flex-direction: column;
    overflow-wrap: break-word;
  }
`

const ItemContent = styled(Body)`
  padding-bottom: 3px;
`

const SectionWrapper = styled.div`
  background: ${theme.colors.white};
  padding: 15px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 5px;
`

const Index = styled(H4)`
  padding-bottom: 3px;
  border: solid 1px;
  margin: 10px 10px 10px 0px;
  padding: 3px 9px 3px 9px;
  border-radius: 5px;
`

// const isFileAnImage = (file) => file.type.indexOf('image') > -1

const DelayedImage = ({ file }) => (
  <a download={file.name} href={`data:${file.type};base64,${file.data}`}>
    {/* {file.type.indexOf('image') > -1 && <img ref={imgRef} alt={file.name} />} */}
    {file.name}
  </a>
)
// const imgRef = useRef()
// const isImage = useMemo(() => isFileAnImage(file), [file.type])

// useEffect(() => {
//   if (isImage) {
//     const blob = b64toBlob(file.data)
//     const FR = new FileReader()
//     FR.addEventListener('load', () => {
//       imgRef.current.src = FR.result
//     })
//     FR.readAsDataURL(blob)
//   }
// }, [])
